import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `<RecordsListContainer />` — async wiring container for the
 * Records tab at `<AccountHomeView />` per CPL Smart Health Account
 * dashboard UX v0.2 §2 records-first IA + §3.1 Records tab canonical.
 * UC1 Patient Access Sprint 9 execution sub-fire per CEO directive
 * 2026-05-10 + VPE Activation ping 22:08Z DEV5 row "UC1 ~60→85%
 * trajectory".
 *
 * # Sprint 9 sandbox-grade scope (this scaffold)
 *
 * Renders **structured record metadata** without content decryption:
 *   - `recordId` (truncated for patient-comprehensibility)
 *   - `contentCategory` (patient-comprehensible category per
 *     `PatientProjectionContentCategory`; NOT PHI per substrate metadata
 *     channel discipline)
 *   - `projectionAnchoredAt` (date when projection record was anchored
 *     at substrate; relative-time rendering at consumer)
 *
 * Record content decryption + clinical-record category extension
 * (Encounter / Lab result / Medication / Imaging / Conditions /
 * Immunizations per CPL §2 wireframe) substrate-state-event-paced:
 *   - `IEnvelopeCryptoAdapter` active-session decryption per
 *     `feedback_phg_client_active_session_crypto.md`
 *   - Rendering taxonomy v0 application (DEV3 portfolio per
 *     Pattern B Issue #51 client-side rendering)
 *   - Clinical category extension at substrate-types canonical
 *     (substrate engineering coordination)
 *
 * # Doctrinal anchors preserved
 *
 *   - NO SHT BACKDOOR (Doctrine v1.3 §8 B.1) — container fetches
 *     structural metadata only; cleartext content does NOT flow through
 *     this surface at v0 (decryption substrate-state-event-paced)
 *   - Recovery UX honesty (`feedback_phg_recovery_ux_honesty.md`) —
 *     "content stays encrypted until rendering taxonomy ships at this
 *     mount" framing; no false-content rendering
 *   - Pattern B no-parallel-rendering-surface — client-side React
 *     state machine; substrate provides metadata via API; client-side
 *     composes + renders
 *   - Active-session-only crypto — KEK/DEK material NOT touched at
 *     this surface; metadata channel only
 *
 * # Composes with
 *
 *   - `IProjectionServiceClient.queryProjectionRecords` existing
 *     wire-contract canonical (`PatientProjectionRequest` shape per
 *     `@shn/substrate-types`; supports `contentCategories` filter +
 *     `anchoredAtRange` time range)
 *   - PR #607 5-tab navigation refactor at `<AccountHomeView />`
 *     (this container mounts at Records tab via `recordsListIntegration`
 *     prop)
 *   - DEV5 CPL Q4 coordination ping at Product-Engineering thread —
 *     records data source wire-contract canonical (DEV5 disposition
 *     PROPOSED + this IMPL composes against existing surface)
 */
import * as React from 'react';
import { useLocale } from '../../i18n/locale-context.js';
function generateRequestId() {
    // ULID-like placeholder; production uses canonical ULID
    // generator at substrate-types per requestId discipline.
    const t = Date.now().toString(36);
    const r = Math.random().toString(36).slice(2, 10);
    return `${t}-${r}`;
}
function truncateRecordId(recordId) {
    if (recordId.length <= 12)
        return recordId;
    return `${recordId.slice(0, 6)}…${recordId.slice(-4)}`;
}
export function RecordsListContainer(props) {
    const { t } = useLocale();
    const { client, subjectPciHash, tenantId, contentCategoryFilter, anchoredAtRange, maxRender } = props;
    const [state, setState] = React.useState({ kind: 'loading' });
    React.useEffect(() => {
        let cancelled = false;
        async function fetchRecords() {
            try {
                const page = await client.queryProjectionRecords({
                    ...(tenantId !== undefined ? { tenantId } : {}),
                    request: {
                        requestId: generateRequestId(),
                        subjectPciHash,
                        ...(contentCategoryFilter !== undefined && contentCategoryFilter.length > 0
                            ? { contentCategories: contentCategoryFilter }
                            : {}),
                        ...(anchoredAtRange !== undefined ? { anchoredAtRange } : {}),
                    },
                });
                if (cancelled)
                    return;
                setState({ kind: 'success', records: page.records });
            }
            catch (err) {
                if (cancelled)
                    return;
                const message = err instanceof Error ? err.message : 'Unknown error';
                setState({ kind: 'error', message });
            }
        }
        void fetchRecords();
        return () => {
            cancelled = true;
        };
    }, [client, subjectPciHash, tenantId, contentCategoryFilter, anchoredAtRange]);
    if (state.kind === 'loading') {
        return (_jsxs("section", { "aria-labelledby": "records-list-heading", className: "records-list-container records-list-container--loading", "data-testid": "records-list-container-loading", children: [_jsx("h3", { id: "records-list-heading", children: t('account.home.records.heading') }), _jsx("p", { className: "records-list-container__loading", children: t('account.home.records.loading') })] }));
    }
    if (state.kind === 'error') {
        return (_jsxs("section", { "aria-labelledby": "records-list-heading", className: "records-list-container records-list-container--error", "data-testid": "records-list-container-error", role: "alert", children: [_jsx("h3", { id: "records-list-heading", children: t('account.home.records.heading') }), _jsx("p", { className: "records-list-container__error", children: t('account.home.records.error_template', { message: state.message }) })] }));
    }
    const records = maxRender !== undefined ? state.records.slice(0, maxRender) : state.records;
    if (records.length === 0) {
        return (_jsxs("section", { "aria-labelledby": "records-list-heading", className: "records-list-container records-list-container--empty", "data-testid": "records-list-container-empty", children: [_jsx("h3", { id: "records-list-heading", children: t('account.home.records.heading') }), _jsx("p", { className: "records-list-container__empty", children: t('account.home.records.empty') })] }));
    }
    return (_jsxs("section", { "aria-labelledby": "records-list-heading", className: "records-list-container", "data-testid": "records-list-container", children: [_jsx("h3", { id: "records-list-heading", children: t('account.home.records.heading') }), _jsx("p", { className: "records-list-container__metadata-only-disclosure", children: t('account.home.records.metadata_only_disclosure') }), _jsx("ul", { className: "records-list", children: records.map((record) => (_jsx("li", { className: "records-list__item", "data-testid": `records-list-item-${record.recordId}`, children: _jsxs("dl", { children: [_jsxs("div", { className: "records-list__row", children: [_jsx("dt", { children: t('account.home.records.category_label') }), _jsx("dd", { children: t(`account.home.records.category.${record.contentCategory}`) })] }), _jsxs("div", { className: "records-list__row", children: [_jsx("dt", { children: t('account.home.records.anchored_at_label') }), _jsx("dd", { children: _jsx("time", { dateTime: record.projectionAnchoredAt, children: record.projectionAnchoredAt }) })] }), _jsxs("div", { className: "records-list__row records-list__row--record-id", children: [_jsx("dt", { children: t('account.home.records.record_id_label') }), _jsx("dd", { children: _jsx("code", { title: record.recordId, children: truncateRecordId(record.recordId) }) })] })] }) }, record.recordId))) })] }));
}
//# sourceMappingURL=records-list-container.js.map